import React, { useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/HighlightOff'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import { LoadingButton } from '@mui/lab'
// MUI components
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack
} from '@mui/material'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import Menu from '@mui/material/Menu'
import { MenuProps } from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { alpha, styled } from '@mui/material/styles'
import { useMutation, useQuery } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import RoundedFilledLoadingButton from 'common/Button/RoundedFilledLoadingButton'
import NormalText from 'common/Typography/NormalText'
import circlesOrbitColorPalette from 'common/ui/circlesColorTheme'
import OrbitSession from 'hooks/useCookies'
import { ICustomerSubHistoryDetails } from 'types/api/faber/subscription.type'

import ErrorSnackbar from '../components/ErrorSnackbar'
import SuccessSnackbar from '../components/SuccessSnackbar'

export const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'right'
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'right'
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 100,
    color:
      theme.palette.mode === 'light'
        ? 'rgb(55, 65, 81)'
        : theme.palette.grey[300],
    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
    '& .MuiMenu-list': {
      padding: '4px 0'
    },
    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5)
      },
      '&:active': {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        )
      }
    }
  }
}))

export default function EditSubscriptionActionMenu({
  getSubHistory,
  refetchSubHistory,
  onSuccessfulFormSubmission
}: EditSubscriptionActionMenuProps) {
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = React.useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const faberToken = OrbitSession.get('faberToken')
  const [isSuccessSnackbarOpen, setSuccessSnackbarOpen] = React.useState(false)
  const [successSnackbarMessage, setSuccessSnackbarMessage] = React.useState('')
  const [isErrorSnackbarOpen, setErrorSnackbarOpen] = React.useState(false)
  const [errorSnackbarMessage, setErrorSnackbarMessage] = useState('')
  const [finalOrderId, setFinalOrderId] = useState('')

  const [isConfirmLoading, setIsConfirmLoading] = useState(false)
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleUpgradeDialogOpen = () => {
    setIsUpgradeDialogOpen(true)
    handleClose()
  }

  const handleApply = () => {
    setIsConfirmDialogOpen(true)
    setIsUpgradeDialogOpen(false)
  }

  const { mutate: upgradeCustomer, isLoading: upgradeCustomerLoading } =
    useMutation(SubscriptionsAPI.upgradeNow, {
      onSuccess: (res) => {
        if (getSubHistory[0].paymentType === 'STRIPE') {
          setIsConfirmDialogOpen(false)
          setSuccessSnackbarOpen(true)
          setSuccessSnackbarMessage(
            'Account has been upgraded successfully! Subscription history will be updated in a few seconds'
          )
          refetchSubHistory()
          onSuccessfulFormSubmission()
        } else if (getSubHistory[0].paymentType === 'PAAS') {
          if (res.data.data.data.orderID) {
            setFinalOrderId(res.data.data.data.orderID)
            setIsConfirmLoading(true)
          } else {
            setErrorSnackbarOpen
            setErrorSnackbarMessage(
              `There was an error creating the order. Please try upgrading customer again.`
            )
          }
        }
      },
      onError: () => {
        setErrorSnackbarOpen(true)
        setErrorSnackbarMessage(
          `Customer's payment failed. Please raise matter up to L3.`
        )
        setIsConfirmDialogOpen(false)
        onSuccessfulFormSubmission()
      }
    })

  useQuery(
    ['getOrderID', finalOrderId],
    async () => {
      const getPaymentStatus = await SubscriptionsAPI.checkOrderId({
        orderID: finalOrderId,
        faberToken: faberToken
      })
      return getPaymentStatus
    },
    {
      onSuccess: (res) => {
        if (
          res.data.data.paymentStatus === 'SUCCESS' ||
          res.data.data.paymentStatus === 'TIMEOUT'
        ) {
          setIsConfirmDialogOpen(false)
          setIsConfirmLoading(false)
          setSuccessSnackbarOpen(true)
          setSuccessSnackbarMessage(
            'Account has been upgraded successfully! Subscription history will be updated in a few seconds'
          )
          onSuccessfulFormSubmission()
        } else if (res.data.data.paymentStatus === 'FAILED') {
          setErrorSnackbarOpen(true)
          setErrorSnackbarMessage(
            `Customer's payment failed. Please raise matter up to L3.`
          )
          setIsConfirmDialogOpen(false)
          setIsConfirmLoading(false)
        }
      },
      onError: () => {
        setIsConfirmDialogOpen(false)
        setErrorSnackbarOpen(true)
        setErrorSnackbarMessage(
          `Customer's payment failed. Please raise matter up to L3.`
        )
        onSuccessfulFormSubmission()
      },
      enabled: !!finalOrderId,
      refetchInterval: isConfirmLoading ? 10000 : false
    }
  )

  const handleConfirmUpgrade = () => {
    upgradeCustomer({
      userId: getSubHistory[0].user_id,
      catalog_bundle_id: 105,
      type: 'UPGRADE',
      faberToken: faberToken
    })
  }

  //TO DO: FIX DOWNGRADE FLOW ONCE UPGRADE NOW IS SETTLED
  // const { mutate: downgradeCustomerSubscription } = useMutation(
  //   SubscriptionsAPI.updateCustomerSubscriptionPlan,
  //   {
  //     onSuccess: () => {
  //       toast.success(`Customer's subscription has been downgraded`)
  //       onSuccessfulFormSubmission()
  //     },
  //     onError: () => {
  //       toast.error('Error in updating customer subscription plan')
  //     }
  //   }
  // )

  // const handleConfirmDowngrade = () => {
  //   downgradeCustomerSubscription({
  //     customerID: getSubHistory[0].user_id,
  //     newBundleID: '101',
  //     faberToken: faberToken
  //   })
  //   handleClose()
  // }

  useEffect(() => {
    setTimeout(() => {
      refetchSubHistory()
    }, 10000)
  }, [isSuccessSnackbarOpen])
  return (
    <>
      <Box>
        <Button
          variant='contained'
          id='fade-button'
          aria-controls={open ? 'fade-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          sx={{ fontSize: '12px', padding: '4px 12px' }}
          endIcon={<KeyboardArrowDownIcon />}
        >
          Edit Subscription
        </Button>
        <StyledMenu
          id='fade-menu'
          MenuListProps={{
            'aria-labelledby': 'fade-button'
          }}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
        >
          {
            getSubHistory[0].planName === 'Lite Pac' ? (
              <MenuItem
                onClick={handleUpgradeDialogOpen}
                sx={{ fontSize: '12px' }}
              >
                <TrendingUpIcon />
                Upgrade
              </MenuItem>
            ) : (
              ''
            )
            //TO DO: FIX DOWNGRADE FLOW
            // (
            //   <MenuItem
            //     sx={{ fontSize: '12px' }}
            //     // onClick={handleConfirmDowngrade}
            //   >
            //     <TrendingDownIcon />
            //     Downgrade
            //   </MenuItem>
            // )
          }
        </StyledMenu>
      </Box>

      <Dialog
        open={isUpgradeDialogOpen}
        onClose={() => {
          setIsUpgradeDialogOpen(false)
        }}
      >
        <Stack flexDirection={'row'} sx={{ justifyContent: 'space-between' }}>
          <DialogTitle>Upgrade Customer</DialogTitle>

          <IconButton
            size='small'
            aria-label='close'
            color='inherit'
            onClick={() => {
              setIsUpgradeDialogOpen(false)
            }}
          >
            <CloseIcon fontSize='small' />
          </IconButton>
        </Stack>
        <DialogContent sx={{ minWidth: '300px' }}>
          <Stack flexDirection={'row'} justifyContent='space-between'>
            <Stack>
              <Paper elevation={6} sx={{ p: 2, borderRadius: '25px' }}>
                <NormalText
                  isBold
                  sx={{ color: circlesOrbitColorPalette.neutrals[500] }}
                >
                  Current Plan
                </NormalText>
                <NormalText sx={{ fontSize: '20px' }}>
                  {getSubHistory[0].planName}
                </NormalText>
                {getSubHistory[0].planName === 'Pro Pac' ? (
                  ''
                ) : (
                  <NormalText>$10/month</NormalText>
                )}
              </Paper>
            </Stack>
            <Stack>
              <Paper elevation={6} sx={{ p: 2, borderRadius: '25px' }}>
                <NormalText
                  isBold
                  sx={{ color: circlesOrbitColorPalette.primary.main }}
                >
                  Upgrading to
                </NormalText>
                <NormalText sx={{ fontSize: '20px' }}>Pro Pac</NormalText>
                <NormalText>$25/month</NormalText>
              </Paper>
            </Stack>
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <RoundedFilledLoadingButton
              variant='filled'
              disabled={getSubHistory[0].planName === 'Pro Pac'}
              sx={{ px: 3, alignItems: 'center' }}
              onClick={handleApply}
            >
              Apply
            </RoundedFilledLoadingButton>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isConfirmDialogOpen}
        onClose={() => {
          setIsConfirmDialogOpen(false)
        }}
      >
        <DialogTitle>Confirm Upgrade</DialogTitle>
        <DialogContent>
          <Alert severity='warning'>
            Please inform customer of the following before you click confirm:
            <List sx={{ listStyleType: 'disc', color: 'black' }}>
              <ListItem>
                &bull; Customer will be charged $15 immediately upon upgrade.
              </ListItem>
              <ListItem>
                &bull; They&apos;ll be charged $25 for the next cycle onwards.{' '}
              </ListItem>
              <ListItem> &bull; Billing cycle date stays the same.</ListItem>
              <ListItem>
                &bull; Bonus data and voucher credits will be added immediately.
              </ListItem>
            </List>
          </Alert>
          <Stack
            flexDirection={'row'}
            sx={{
              mt: 2,
              justifyContent: 'space-evenly'
            }}
          >
            <LoadingButton
              onClick={() => {
                setIsConfirmDialogOpen(false)
              }}
            >
              Cancel
            </LoadingButton>
            <RoundedFilledLoadingButton
              sx={{ px: '25px' }}
              loading={upgradeCustomerLoading || isConfirmLoading}
              loadingIndicator={
                <Stack flexDirection={'row'} sx={{ justifyContent: 'center' }}>
                  <CircularProgress
                    sx={{ color: 'white', mt: '10px', mx: 0.5 }}
                    size={20}
                  />
                  <NormalText
                    sx={{
                      color: 'white'
                      // ml: '5px'
                    }}
                  >
                    Upgrading...
                  </NormalText>
                </Stack>
              }
              onClick={handleConfirmUpgrade}
            >
              Confirm
            </RoundedFilledLoadingButton>
          </Stack>
        </DialogContent>
      </Dialog>
      <SuccessSnackbar
        isSuccessSnackbarOpen={isSuccessSnackbarOpen}
        setSuccessSnackbarOpen={setSuccessSnackbarOpen}
        message={successSnackbarMessage}
      />
      <ErrorSnackbar
        setErrorSnackbarOpen={setErrorSnackbarOpen}
        isErrorSnackbarOpen={isErrorSnackbarOpen}
        message={errorSnackbarMessage}
      />
    </>
  )
}

interface EditSubscriptionActionMenuProps {
  getSubHistory: ICustomerSubHistoryDetails[]
  refetchSubHistory: () => void
  onSuccessfulFormSubmission: () => void
}
